import { useState, useCallback } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { RefundFilterBar } from './components/RefundFilterBar'
import { RefundTable } from './components/RefundTable'
import { RefundDetailDrawer } from './components/RefundDetailDrawer'
import { RefundProcessDialog } from './components/RefundProcessDialog'
import { useSupplierRefunds, useSupplierRefund, useRefundProcess, useRefundConfirmReceive } from '@/hooks/useSupplierRefund'
import { showError } from '@/lib/error'
import type { RefundListParams, Refund, RefundProcessParams } from '@/schemas/supplier'

export function SupplierRefundsPage() {
  const [params, setParams] = useState<RefundListParams>({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
  })
  const [detailRefundId, setDetailRefundId] = useState<number | null>(null)
  const [processRefund, setProcessRefund] = useState<Refund | null>(null)

  // 查询退款列表
  const { data, isLoading, refetch } = useSupplierRefunds(params)

  // 详情查询
  const { data: detailRefund, isLoading: isDetailLoading } = useSupplierRefund(
    detailRefundId ?? 0,
    detailRefundId !== null
  )

  // Mutations
  const processMutation = useRefundProcess()
  const confirmReceiveMutation = useRefundConfirmReceive()

  // 处理查看详情
  const handleViewDetail = useCallback((refund: Refund) => {
    setDetailRefundId(refund.id)
  }, [])

  // 处理退款
  const handleProcess = useCallback((refund: Refund) => {
    setProcessRefund(refund)
  }, [])

  // 处理退款确认
  const handleProcessConfirm = useCallback((data: RefundProcessParams) => {
    if (!processRefund) return
    processMutation.mutate(
      { id: processRefund.id, data },
      {
        onSuccess: () => setProcessRefund(null),
        onError: (error) => showError(error, '处理退款失败'),
      }
    )
  }, [processRefund, processMutation])

  // 确认收货
  const handleConfirmReceive = useCallback((refundId: number) => {
    confirmReceiveMutation.mutate(refundId, {
      onSuccess: () => setDetailRefundId(null),
      onError: (error) => showError(error, '确认收货失败'),
    })
  }, [confirmReceiveMutation])

  // 更新筛选参数
  const handleParamsChange = useCallback((newParams: Partial<RefundListParams>) => {
    setParams((prev) => ({
      ...prev,
      ...newParams,
      page: 1,
    }))
  }, [])

  // 分页处理
  const handlePageChange = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }))
  }, [])

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setParams((prev) => ({ ...prev, page: 1, page_size: pageSize }))
  }, [])

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">退款管理</h1>
          <p className="text-muted-foreground">管理供应商退款申请和处理</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-1.5 h-4 w-4" />
            刷新
          </Button>
        </div>
      </div>

      {/* 筛选栏 */}
      <RefundFilterBar params={params} onParamsChange={handleParamsChange} />

      {/* 退款表格 */}
      <RefundTable
        refunds={data?.list || []}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onProcess={handleProcess}
      />

      {/* 分页 */}
      <ListPagination
        page={params.page}
        pageSize={params.page_size}
        total={data?.total || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* 退款详情抽屉 */}
      <RefundDetailDrawer
        refund={isDetailLoading ? null : detailRefund ?? null}
        open={detailRefundId !== null}
        onOpenChange={(open) => !open && setDetailRefundId(null)}
        onConfirmReceive={handleConfirmReceive}
        isConfirmReceiveLoading={confirmReceiveMutation.isPending}
      />

      {/* 退款处理对话框 */}
      {processRefund && (
        <RefundProcessDialog
          refund={processRefund}
          open={!!processRefund}
          onOpenChange={(open) => !open && setProcessRefund(null)}
          onConfirm={handleProcessConfirm}
          isLoading={processMutation.isPending}
        />
      )}
    </div>
  )
}

export { RefundFilterBar } from './components/RefundFilterBar'
export { RefundTable } from './components/RefundTable'
